import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api';
import { getInitData } from '../lib/auth';
import { tg } from '../lib/tg';
import type {
  BalanceResponse,
  BotTogglePayload,
  DepositAddressResponse,
  DepositHistoryResponse,
  DepositIntentRequest,
  DepositIntentResponse,
  EarningsListResponse,
  HlBalanceResponse,
  InternalTransfersListResponse,
  MyBalancesResponse,
  PlatformConfig,
  PmMarketsForMeResponse,
  PositionsResponse,
  ReferralMembersResponse,
  ReferralSummary,
  TransferRowDetail,
  TransferRowItem,
  TransfersListResponse,
  UserProfile,
  WithdrawalItem,
  WithdrawalsListResponse,
  WithdrawalSource,
} from '../lib/types';

export function useMe() {
  // Refetch on window focus + reconnect so admin overrides
  // (platform_fee_pct_override etc.) propagate without forcing
  // the user to manually reload the webapp. Without this the
  // /me response was cached for the entire session.
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<UserProfile>('/v1/me'),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 30_000,
  });
}

export function usePlatformConfig() {
  // Public unauthenticated endpoint; small payload (couple of
  // Decimals). Refetch on focus so admin tweaks to the
  // withdrawal fee or min_deposit propagate without a hard reload.
  return useQuery({
    queryKey: ['platform-config'],
    queryFn: () => api.get<PlatformConfig>('/v1/platform-config'),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 60_000,
  });
}

export function useBotToggle() {
  const qc = useQueryClient();
  return useMutation({
    // At least one of `pm` / `hl` must be present — the API enforces
    // this with a 422; callers should treat both-undefined as a bug.
    mutationFn: (payload: BotTogglePayload) =>
      api.patch<{
        pm: { enabled: boolean; available: boolean; admin_paused: boolean };
        hl: { enabled: boolean; available: boolean; admin_paused: boolean };
      }>('/v1/me/bot', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export type UiThemePref = 'light' | 'dark' | 'system';

export interface NotificationPrefs {
  prefs: Record<string, boolean>;
}

export function useNotificationPrefs() {
  // Pull on Settings sheet open; cache-friendly because the user
  // rarely changes prefs more than once per session.
  return useQuery({
    queryKey: ['notification-prefs'],
    queryFn: () => api.get<NotificationPrefs>('/v1/me/notification-prefs'),
    staleTime: 60_000,
  });
}

export function usePatchNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (partial: Record<string, boolean>) =>
      api.patch<NotificationPrefs>('/v1/me/notification-prefs', {
        prefs: partial,
      }),
    onSuccess: (data) => {
      // Replace the cache with the server-merged response so the
      // toggle row reflects merge semantics (preserves other prefs)
      // without a follow-up GET.
      qc.setQueryData(['notification-prefs'], data);
    },
  });
}

export function useUpdatePreferences() {
  // No queryClient invalidate — local state already flipped (the
  // ThemeProvider drives the data-theme attribute optimistically).
  // The PATCH is a fire-and-forget sync so the choice survives
  // re-installs / cross-device.
  return useMutation({
    mutationFn: (patch: { ui_theme?: UiThemePref }) =>
      api.patch<{ ui_theme: UiThemePref }>('/v1/me/preferences', patch),
  });
}

export function useBalance() {
  return useQuery({
    queryKey: ['balance'],
    queryFn: () => api.get<BalanceResponse>('/v1/balance'),
    refetchInterval: 15_000,
  });
}

// HL withdrawable + account_value. Only the Wallet "Move funds" page
// reads this — the dashboard sticks to /v1/balance (Polygon pUSD) so
// users without HL never pay for an HL Info round-trip.
//
// ``enabled`` lets the caller skip the fetch entirely (e.g. the
// transfer wizard only enables it on direction = HL→PM).
export function useHlBalance(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['balance-hl'],
    queryFn: () => api.get<HlBalanceResponse>('/v1/balance/hl'),
    enabled: opts.enabled ?? true,
    refetchInterval: 15_000,
  });
}

export function useInternalTransfers() {
  return useQuery({
    queryKey: ['internal-transfers'],
    queryFn: () =>
      api.get<InternalTransfersListResponse>('/v1/internal-transfers'),
    refetchInterval: 10_000,
  });
}

// ─── Architecture C balances + transfers ────────────────
//
// 3-tile balance view (main wallet + PM bot bankroll + HL bot bankroll
// + in-transit list) used by the Wallet page header. Polls aggressively
// because allocate / deallocate flows expect to see new in-transit
// rows show up within a couple seconds.
export function useMyBalances() {
  return useQuery({
    queryKey: ['my-balances'],
    queryFn: () => api.get<MyBalancesResponse>('/v1/me/balances'),
    refetchInterval: 10_000,
  });
}

export function useTransfers() {
  return useQuery({
    queryKey: ['transfers'],
    queryFn: () => api.get<TransfersListResponse>('/v1/transfers'),
    refetchInterval: 10_000,
  });
}

// One-row poll for the in-transit indicator. Faster cadence than the
// list — driven by the user staring at the spinner.
export function useTransferDetail(transferId: string | null) {
  return useQuery({
    queryKey: ['transfer-detail', transferId],
    queryFn: () =>
      api.get<TransferRowDetail>(`/v1/transfers/${transferId}`),
    enabled: !!transferId,
    refetchInterval: 5_000,
  });
}

export interface AllocateRequestBody {
  source_token: string;
  source_chain: 'polygon' | 'arbitrum';
  amount: string;
  // MAX / swap-all: move the whole source stable (worker records the
  // actually-landed amount). Only honoured under transfer_live_quote_enabled.
  max_source_swap?: boolean;
}

export interface TransferQuoteResponse {
  max_transferable_usdc: string;
  canonical_covered_usdc: string;
  needs_swap: boolean;
}

// Real max allocatable to a bot in one op (live-quote or legacy buffer,
// per transfer_live_quote_enabled). Only enabled when the caller opts in
// (the flag is off) — polls alongside balances so the number stays fresh.
export function useTransferQuote(
  direction: string,
  chainId: number,
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['transfer-quote', direction, chainId],
    queryFn: () =>
      api.get<TransferQuoteResponse>(
        `/v1/transfers/quote?direction=${direction}&chain=${chainId}`,
      ),
    enabled: opts.enabled ?? true,
    refetchInterval: 10_000,
  });
}

export interface DeallocateRequestBody {
  amount: string;
}

export interface CrossBotRequestBody {
  direction: 'pm_to_hl' | 'hl_to_pm';
  amount: string;
}

export const allocateToPm = (req: AllocateRequestBody): Promise<TransferRowItem> =>
  api.post<TransferRowItem>('/v1/transfers/allocate-pm', req as unknown as Record<string, unknown>);

export const allocateToHl = (req: AllocateRequestBody): Promise<TransferRowItem> =>
  api.post<TransferRowItem>('/v1/transfers/allocate-hl', req as unknown as Record<string, unknown>);

export const deallocateFromPm = (req: DeallocateRequestBody): Promise<TransferRowItem> =>
  api.post<TransferRowItem>('/v1/transfers/deallocate-pm', req as unknown as Record<string, unknown>);

export const deallocateFromHl = (req: DeallocateRequestBody): Promise<TransferRowItem> =>
  api.post<TransferRowItem>('/v1/transfers/deallocate-hl', req as unknown as Record<string, unknown>);

export const crossBotTransfer = (req: CrossBotRequestBody): Promise<TransferRowItem> =>
  api.post<TransferRowItem>('/v1/transfers', req as unknown as Record<string, unknown>);

export type DepositService = 'pm' | 'hl' | 'both';

export function useDepositAddress(service?: DepositService) {
  const qs = service ? `?service=${service}` : '';
  return useQuery({
    queryKey: ['deposit-address', service ?? 'all'],
    queryFn: () =>
      api.get<DepositAddressResponse>(`/v1/deposits/address${qs}`),
    staleTime: Infinity,
  });
}

export function useDepositHistory() {
  return useQuery({
    queryKey: ['deposits'],
    queryFn: () => api.get<DepositHistoryResponse>('/v1/deposits'),
    refetchInterval: 10_000,
  });
}

// Pre-stages a Deposit row with the user's allocation_intent so the
// downstream worker (swap_executor + CCTP fan-out) knows how to route
// the funds when they actually land on-chain. Only used for the Both
// service path — single-service deposits use the legacy implicit
// routing.
export function useCreateDepositIntent() {
  return useMutation({
    mutationFn: (req: DepositIntentRequest) =>
      api.post<DepositIntentResponse>(
        '/v1/deposits/intent',
        req as unknown as Record<string, unknown>,
      ),
  });
}

export function useWithdrawals() {
  return useQuery({
    queryKey: ['withdrawals'],
    queryFn: () => api.get<WithdrawalsListResponse>('/v1/withdrawals'),
    refetchInterval: 30_000,
  });
}

export interface WithdrawalParams {
  to_address: string;
  amount_usd: string;
  /** Defaults to 'eoa' server-side. 'pm' (legacy Arch-B) is rejected with 400. */
  source?: Exclude<WithdrawalSource, 'pm'>;
  /** Required when source='eoa' — picks Polygon (137) or Arbitrum (42161). */
  chain_id?: number;
  /** Required when source='eoa' — token symbol on the chosen chain. */
  token?: string;
}

export type CreateWithdrawalFn = (
  req: WithdrawalParams & {
    /** Browser cabinet only: the 6-digit code the bot sent to Telegram. */
    stepup_code?: string;
    /** MAX / swap-all (source='eoa'): swap the whole source stable so the
     *  requested amount lands robustly. Submit-only — not part of the
     *  challenge params / OTP fingerprint. */
    max_source_swap?: boolean;
  },
) => Promise<WithdrawalItem>;

// Step-up auth — surface-dependent:
//   TG WebApp → freshly-signed ``initData`` (HMAC-verified, <5 min old).
//   browser cabinet → ``stepup_code`` the bot delivered to Telegram.
// Either way a stolen JWT alone can't drain the wallet: forging
// fresh initData needs the user's TG session, and the OTP only ever
// lands in the user's Telegram, never the browser.
export const createWithdrawal: CreateWithdrawalFn = (req) => {
  const { stepup_code, ...rest } = req;
  const proof = tg.inTelegram
    ? { init_data: getInitData() }
    : { stepup_code };
  return api.post<WithdrawalItem>('/v1/withdrawals', { ...rest, ...proof });
};

// Browser cabinet: ask the bot to send a step-up code to the user's
// Telegram for these exact params. The code is bound to them server-side,
// so the subsequent createWithdrawal must carry identical params.
export const requestWithdrawalChallenge = (
  req: WithdrawalParams,
): Promise<{ ok: boolean; expires_in: number }> =>
  api.post<{ ok: boolean; expires_in: number }>(
    '/v1/withdrawals/challenge',
    { ...req },
  );

export function useReferralSummary() {
  return useQuery({
    queryKey: ['referrals-summary'],
    queryFn: () => api.get<ReferralSummary>('/v1/referrals'),
    refetchInterval: 30_000,
  });
}

export function useReferralMembers() {
  return useQuery({
    queryKey: ['referrals-members'],
    queryFn: () =>
      api.get<ReferralMembersResponse>('/v1/referrals/members'),
    refetchInterval: 30_000,
  });
}

export interface ReferralEarningsFilters {
  level?: 1 | 2;
  source_user_id?: string;
  from_ts?: string;
  to_ts?: string;
}

export function useReferralEarnings(filters: ReferralEarningsFilters = {}) {
  const params = new URLSearchParams();
  if (filters.level) params.set('level', String(filters.level));
  if (filters.source_user_id)
    params.set('source_user_id', filters.source_user_id);
  if (filters.from_ts) params.set('from_ts', filters.from_ts);
  if (filters.to_ts) params.set('to_ts', filters.to_ts);
  const qs = params.toString();
  return useQuery({
    queryKey: ['referrals-earnings', filters],
    queryFn: () =>
      api.get<EarningsListResponse>(
        `/v1/referrals/earnings${qs ? `?${qs}` : ''}`,
      ),
    refetchInterval: 30_000,
  });
}

export function usePositions(params?: {
  status?: 'open' | 'closed';
  bot_kind?: 'polymarket' | 'hyperliquid';
}) {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.bot_kind) qs.set('bot_kind', params.bot_kind);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';

  return useQuery({
    queryKey: ['positions', params?.status ?? 'all', params?.bot_kind ?? 'all'],
    queryFn: () => api.get<PositionsResponse>(`/v1/positions${suffix}`),
    refetchInterval: 15_000,
  });
}

// 5 min staleTime + window-focus refetch: admin rarely changes the
// allowlist mid-session, but the next focus picks up "admin just
// enabled ETH 5m for me" without a hard reload.
export function useMyPmMarkets() {
  return useQuery({
    queryKey: ['pm-markets'],
    queryFn: () => api.get<PmMarketsForMeResponse>('/v1/pm/markets'),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
